import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const ENDPOINTS = [
  { method: 'GET', path: '/products', desc: 'Liste paginée des produits publiés (prix, stock, variantes).' },
  { method: 'GET', path: '/products/:id', desc: 'Détail d’un produit avec ses variantes et ses images.' },
  { method: 'POST', path: '/orders', desc: 'Crée une commande et retourne son identifiant.' },
  { method: 'GET', path: '/orders/:id', desc: 'Statut, lignes et paiement d’une commande.' },
  { method: 'POST', path: '/invoices', desc: 'Émet une facture conforme (numérotation continue).' },
  { method: 'GET', path: '/invoices/:id/pdf', desc: 'Télécharge la facture au format PDF.' },
  { method: 'POST', path: '/credits', desc: 'Enregistre une vente à crédit dans le carnet.' },
  { method: 'GET', path: '/customers', desc: 'Liste des clients du commerce connecté.' },
];

const METHOD_STYLES: Record<string, string> = {
  GET: 'bg-primary-100 text-primary-700',
  POST: 'bg-accent-100 text-accent-800',
  PATCH: 'bg-secondary-100 text-secondary-900',
  DELETE: 'bg-background-200/70 text-foreground-700',
};

const SAMPLE = `curl -X GET "https://api.zifek.fr/v1/products?limit=20" \\
  -H "Authorization: Bearer VOTRE_CLE_API" \\
  -H "X-Commerce-Id: 12345"`;

export default function ApiPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar forceScrolled />

      <header className="pt-24 md:pt-32 pb-10 px-4 md:px-6 lg:px-10 bg-foreground-950">
        <div className="max-w-6xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-background-50 text-xs font-semibold">
            <i className="ri-terminal-line"></i>API Zifek
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold font-heading text-background-50 leading-tight max-w-3xl">
            Connectez vos outils à votre commerce
          </h1>
          <p className="mt-3 text-sm md:text-base text-background-50/60 max-w-2xl">
            Une API REST simple, en JSON, pour synchroniser produits, commandes, factures et clients avec
            votre logiciel de gestion ou votre site existant.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            {['REST / JSON', 'Authentification par clé', 'Limite 120 req/min', 'Webhooks'].map((tag) => (
              <span key={tag} className="px-3 py-1.5 rounded-full bg-white/10 text-background-50/80 font-medium">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-10 py-10 md:py-14">
        <div className="max-w-6xl mx-auto space-y-8">
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
              <h2 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2 mb-3">
                <i className="ri-key-2-line text-primary-500"></i>Authentification
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Chaque commerce possède une clé API générée depuis son espace. Envoyez-la dans l’en-tête
                <code className="mx-1 px-1.5 py-0.5 rounded bg-background-100 text-[12px] text-foreground-800">Authorization</code>
                au format <strong className="text-foreground-900">Bearer</strong>. Toute requête sans clé valide est refusée.
              </p>
              <div className="mt-4 rounded-md bg-background-100 p-3 text-[12px] text-foreground-600">
                Ne partagez jamais votre clé dans du code front-end : utilisez un serveur ou une fonction edge.
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
              <h2 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2 mb-3">
                <i className="ri-shield-check-line text-accent-600"></i>Bonnes pratiques
              </h2>
              <ul className="space-y-2 text-sm text-foreground-600">
                {[
                  'Paginez systématiquement (limit / offset).',
                  'Utilisez un identifiant d’idempotence pour les écritures.',
                  'Surveillez le code 429 et appliquez un backoff.',
                  'Vérifiez la signature des webhooks avant traitement.',
                ].map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <i className="ri-check-line text-primary-500 mt-0.5"></i>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4">Points d’entrée principaux</h2>
            <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="hidden md:grid grid-cols-[80px_1fr_1.4fr] gap-4 px-5 py-3 bg-background-100 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                <span>Méthode</span>
                <span>Chemin</span>
                <span>Description</span>
              </div>
              <div className="divide-y divide-background-200/70">
                {ENDPOINTS.map((e) => (
                  <div key={`${e.method}-${e.path}`} className="grid grid-cols-1 md:grid-cols-[80px_1fr_1.4fr] gap-2 md:gap-4 px-5 py-3 items-center">
                    <span className={`inline-flex justify-center px-2 py-1 rounded-md text-[11px] font-bold w-fit ${METHOD_STYLES[e.method]}`}>
                      {e.method}
                    </span>
                    <code className="text-[12px] text-foreground-900 font-mono break-all">{e.path}</code>
                    <span className="text-sm text-foreground-600">{e.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <h2 className="text-lg font-bold font-heading text-foreground-950 mb-3">Exemple de requête</h2>
              <pre className="bg-foreground-950 text-background-50/90 rounded-lg p-4 md:p-5 overflow-x-auto text-[12px] leading-relaxed">
                <code>{SAMPLE}</code>
              </pre>
            </div>
            <div>
              <h2 className="text-lg font-bold font-heading text-foreground-950 mb-3">Réponse</h2>
              <pre className="bg-background-100 text-foreground-800 rounded-lg p-4 md:p-5 overflow-x-auto text-[12px] leading-relaxed border border-background-200/70">
                <code>{`{
  "data": [
    {
      "id": 100001,
      "name": "Sac en cuir artisanal",
      "price": 420,
      "currency": "MAD",
      "stock": 12,
      "status": "active"
    }
  ],
  "meta": { "total": 1, "limit": 20, "offset": 0 }
}`}</code>
              </pre>
            </div>
          </section>

          <div className="bg-secondary-100 rounded-lg p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold font-heading text-secondary-900">Obtenir une clé API</h3>
              <p className="text-sm text-secondary-900/70 mt-1">
                La clé est disponible dans votre espace, rubrique Zifek Core → API.
              </p>
            </div>
            <span className="px-5 py-2.5 rounded-full bg-background-50 text-foreground-900 text-sm font-semibold whitespace-nowrap">
              Dashboard → Core → API
            </span>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}